// A/B experiment assignment.
//
// Variants are assigned once per browser and persisted in localStorage (with
// sessionStorage / in-memory fallbacks, same pattern as visitorId.js), so a
// visitor stays in their arm across sessions. Assignments are sent to the
// backend inside the search payload (`experiments`) and mirrored to GA4 as
// user properties (`ab_<key>`), which lets the SavedFlightSearch/UserEvent
// tables slice funnels by variant.
//
// Overrides, in precedence order (both persist so QA is sticky):
//   ?ab_<key>=<variant>         — force a variant from the URL
//   VUE_APP_AB_<KEY>=<variant>  — force a variant for everyone at build time
//   VUE_APP_AB_<KEY>=off        — disable the experiment entirely

export const EXPERIMENTS = {
  search_form_dates: { variants: ['control', 'exact_default'], weights: [50, 50] },
};

const STORAGE_KEY = 'pfExperiments';

let cache = null;

const readStored = () => {
  if (cache) return cache;
  for (const store of [safeLocalStorage(), safeSessionStorage()]) {
    try {
      const raw = store && store.getItem(STORAGE_KEY);
      if (raw) {
        cache = JSON.parse(raw) || {};
        return cache;
      }
    } catch (_) {
      /* fall through */
    }
  }
  cache = {};
  return cache;
};

const writeStored = (map) => {
  cache = map;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    return;
  } catch (_) {
    /* localStorage unavailable */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (_) {
    /* in-memory only */
  }
};

function safeLocalStorage() {
  try { return localStorage; } catch (_) { return null; }
}

function safeSessionStorage() {
  try { return sessionStorage; } catch (_) { return null; }
}

const randomFraction = () => {
  try {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] / 0x100000000;
  } catch (_) {
    return Math.random();
  }
};

const weightedPick = ({ variants, weights }) => {
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = randomFraction() * total;
  for (let i = 0; i < variants.length; i++) {
    roll -= weights[i];
    if (roll < 0) return variants[i];
  }
  return variants[variants.length - 1];
};

const envOverride = (key) =>
  process.env[`VUE_APP_AB_${key.toUpperCase()}`] || null;

const urlOverride = (key) => {
  try {
    const value = new URLSearchParams(window.location.search).get(`ab_${key}`);
    return value ? value.trim().toLowerCase() : null;
  } catch (_) {
    return null;
  }
};

const isDisabled = (key) => envOverride(key) === 'off';

const setGtagUserProperty = (key, variant) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_properties', { [`ab_${key}`]: variant });
    }
  } catch (_) {
    /* gtag unavailable */
  }
};

/**
 * Returns the visitor's variant for an experiment, assigning one if needed.
 * Unknown keys and disabled experiments return 'control'.
 */
export function getVariant(key) {
  const config = EXPERIMENTS[key];
  if (!config || isDisabled(key)) return 'control';

  const forced = urlOverride(key) || envOverride(key);
  if (forced && config.variants.includes(forced)) {
    const stored = readStored();
    if (stored[key] !== forced) {
      writeStored({ ...stored, [key]: forced });
    }
    setGtagUserProperty(key, forced);
    return forced;
  }

  const stored = readStored();
  if (config.variants.includes(stored[key])) {
    setGtagUserProperty(key, stored[key]);
    return stored[key];
  }

  const variant = weightedPick(config);
  writeStored({ ...stored, [key]: variant });
  setGtagUserProperty(key, variant);
  return variant;
}

/**
 * {key: variant} for every non-disabled experiment, assigning where needed.
 * Safe to attach to API payloads verbatim.
 */
export function getActiveExperiments() {
  const active = {};
  for (const key of Object.keys(EXPERIMENTS)) {
    if (isDisabled(key)) continue;
    active[key] = getVariant(key);
  }
  return active;
}

/** Same data shaped as GA4 user properties ({ab_<key>: variant}). */
export function activeExperimentsAsUserProps() {
  const props = {};
  for (const [key, variant] of Object.entries(getActiveExperiments())) {
    props[`ab_${key}`] = variant;
  }
  return props;
}

/** Clears stored assignments. */
export function clearExperiments() {
  cache = {};
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
}
